import { getAirPathBoundsPoints } from "@project/floorplanner/konva/air-path-geometry-konva";
import { FloorplanItemTextKonva } from "@project/floorplanner/konva/floorplan-item-text-konva";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { LabelLayoutKonva } from "@project/floorplanner/konva/label-layout-konva";
import { Group } from "konva/lib/Group";
import { Arrow } from "konva/lib/shapes/Arrow";

import { AirPath, AirPathDirection } from "@domain/project/floorplan/air-path";
import Konva from "konva";

export class AirPathKonva {
  static init(airPath: AirPath): Group {
    const color = airPath.direction == AirPathDirection.SUPPLY_AIR ? "blue" : "red";
    const basePointerLength = 15;
    const basePointerWidth = 15;

    const parentGroup = new Group();

    const airPathGroup = new Group({
      x: airPath.x,
      y: airPath.y,
      width: airPath.length,
      rotation: airPath.rotation,
      draggable: false,
    });
    airPathGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, airPath);
    airPathGroup.setAttr("minWidth", 70);
    airPathGroup.setAttr("minHeight", 20);
    const airPathShape = new Arrow({
      x: 0,
      y: 0,
      points: [0, 0, airPath.length, 0],
      pointerLength: basePointerLength,
      pointerWidth: basePointerWidth,
      fill: color,
      stroke: color,
      strokeWidth: airPath.strokeWidth,
    });
    airPathShape.setAttr("basePointerLength", basePointerLength);
    airPathShape.setAttr("basePointerWidth", basePointerWidth);
    const boundsRect = this.createArrowBoundsRect();
    airPathGroup.height(airPathShape.strokeWidth());
    airPathGroup.add(boundsRect, airPathShape);
    this.updateArrowBoundsRect(boundsRect, airPathShape);

    const textGroup = FloorplanItemTextKonva.init(airPath);
    this.scaleText(airPathGroup, textGroup);

    parentGroup.add(airPathGroup, textGroup);
    this.setTextPosition(airPathGroup, textGroup);
    airPathGroup.on("transformstart", () => {
      textGroup.hide();
    });

    airPathGroup.on("transform", () => {
      const scaleX = airPathGroup.scaleX();
      const scaleY = airPathGroup.scaleY();
      airPathGroup.width(airPathGroup.width() * airPathGroup.scaleX());

      airPathShape.points([0, 0, airPathGroup.width(), 0]);
      airPathShape.strokeWidth(airPathShape.strokeWidth() * scaleY);
      const pointerLength = basePointerLength * scaleX;
      const pointerWidth = basePointerWidth * scaleY;
      airPathShape.pointerLength(pointerLength);
      airPathShape.pointerWidth(pointerWidth);

      airPathGroup.height(airPathShape.strokeWidth());
      this.updateArrowBoundsRect(boundsRect, airPathShape);

      this.setTextPosition(airPathGroup, textGroup);
      this.scaleText(airPathGroup, textGroup);

      airPathGroup.scaleX(1);
      airPathGroup.scaleY(1);
      textGroup.rotation(0);
    });

    airPathGroup.on("transformend", () => {
      airPath.updateForm(
        airPathGroup.width(),
        airPathGroup.height(),
        airPathGroup.rotation(),
        airPathGroup.x(),
        airPathGroup.y(),
      );
      const labelHidden = textGroup.getAttr("labelHidden") === true;
      if (!labelHidden) {
        textGroup.show();
      }
      this.setTextPosition(airPathGroup, textGroup);
    });

    airPathGroup.on("dragmove", () => {
      this.setTextPosition(airPathGroup, textGroup);
    });

    return parentGroup;
  }

  static refreshLength(airPath: AirPath, parentGroup: Group) {
    const airPathGroup = parentGroup.getChildren()[0] as Group;
    const boundsRect = airPathGroup.getChildren()[0] as Konva.Rect;
    const arrow = airPathGroup.getChildren()[1] as Arrow;
    airPathGroup.width(airPath.length);
    arrow.points([0, 0, airPathGroup.width(), 0]);
    const basePointerLength = arrow.getAttr("basePointerLength") ?? arrow.pointerLength();
    const basePointerWidth = arrow.getAttr("basePointerWidth") ?? arrow.pointerWidth();
    arrow.pointerLength(basePointerLength);
    arrow.pointerWidth(basePointerWidth);
    this.updateArrowBoundsRect(boundsRect, arrow);
    const textGroup = parentGroup.getChildren()[1] as Konva.Group;
    this.refreshItemText(airPath, airPathGroup, textGroup);
  }

  static refreshItemText(airPath: AirPath, airPathGroup: Group, textGroup: Group) {
    FloorplanItemTextKonva.refreshText(airPath, textGroup);
    FloorplanItemTextKonva.updateBackground(textGroup);
    this.setTextPosition(airPathGroup, textGroup);
  }

  private static setTextPosition(airPathGroup: Group, textGroup: Group) {
    FloorplanItemTextKonva.updateBackground(textGroup);
    const item = airPathGroup.getAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY);
    LabelLayoutKonva.placeLabelForItem(textGroup, airPathGroup, item);
  }

  private static scaleText(airPathGroup: Group, textGroup: Group) {
    const itemText = textGroup.getChildren()[1] as Konva.Text;
    const airPathShape = airPathGroup.getChildren()[1] as Konva.Shape;
    itemText.fontSize(airPathShape.strokeWidth() / 2);
    FloorplanItemTextKonva.scaleTextGroup(textGroup, airPathGroup);
    FloorplanItemTextKonva.updateBackground(textGroup);
  }

  private static createArrowBoundsRect() {
    return new Konva.Rect({
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      fill: "transparent",
      opacity: 0,
      listening: false,
    });
  }

  private static updateArrowBoundsRect(boundsRect: Konva.Rect, arrow: Arrow) {
    const arrowPoints = arrow.points();
    if (arrowPoints.length < 4) {
      return;
    }

    const lineStart = { x: arrowPoints[0], y: arrowPoints[1] };
    const lineEnd = { x: arrowPoints[arrowPoints.length - 2], y: arrowPoints[arrowPoints.length - 1] };
    const headLength = arrow.pointerLength();
    const headWidth = arrow.pointerWidth();
    const showHead = arrow.pointerAtEnding();
    const strokeJoinLimit = arrow.miterLimit() ?? 10;
    const { points, halfStroke } = getAirPathBoundsPoints(
      lineStart,
      lineEnd,
      headLength,
      headWidth,
      showHead,
      arrow.strokeWidth() ?? 0,
      strokeJoinLimit,
    );

    if (points.length === 0) {
      return;
    }

    this.applyBoundsFromPoints(boundsRect, points, halfStroke);
  }

  private static applyBoundsFromPoints(boundsRect: Konva.Rect, points: { x: number; y: number }[], halfStroke: number) {
    const xs = points.map((point) => point.x);
    const ys = points.map((point) => point.y);
    const minX = Math.min(...xs) - halfStroke;
    const maxX = Math.max(...xs) + halfStroke;
    const minY = Math.min(...ys) - halfStroke;
    const maxY = Math.max(...ys) + halfStroke;

    boundsRect.x(minX);
    boundsRect.y(minY);
    boundsRect.width(maxX - minX);
    boundsRect.height(maxY - minY);
  }
}
